import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { Heart, Clock, Users, ChefHat, Sparkles, Search, Wand2, RefreshCw } from "lucide-react";

function FavBtn({ slug, isFav, onChange, "data-testid": testId = "recipe-fav" }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user) return null;
  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post(`/recipes/${slug}/favorite`);
      onChange?.(data.is_favorite);
    } finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy} data-testid={testId}
            className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${isFav ? "bg-[var(--ff-citrus)] text-[var(--ff-text-primary)]" : "bg-white/90 backdrop-blur text-[var(--ff-text-muted)] hover:text-[var(--ff-primary)]"}`}>
      <Heart size={16} fill={isFav ? "currentColor" : "none"}/>
    </button>
  );
}

function SmartSuggestions() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (force = false) => {
    if (!user) return;
    setBusy(true);
    try {
      const { data: res } = force
        ? await api.post("/recipes/suggest/refresh")
        : await api.get("/recipes/suggest");
      setData(res);
    } catch {
      setData({ suggestions: [] });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (user) load(false); /* eslint-disable-next-line */ }, [user?.id]);

  if (!user) return null;
  if (!data && !busy) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 pt-12 pb-2" data-testid="smart-suggestions">
      <div
        className="rounded-2xl text-white p-8 md:p-10 shadow-xl"
        style={{ background: "linear-gradient(135deg, var(--ff-primary) 0%, #0d2e22 100%)" }}
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <p className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--ff-citrus)]">
              <Wand2 size={12}/> Smart picks · powered by AI
            </p>
            <h2 className="font-display text-3xl md:text-4xl mt-2">From your pantry, by your chef.</h2>
            {data?.pantry?.length > 0 && (
              <p className="text-sm text-white/70 mt-2 max-w-2xl">
                Based on your last orders: <span className="text-white/90">{data.pantry.slice(0, 4).join(" · ")}</span>
                {data.pantry.length > 4 && <span className="text-white/50"> +{data.pantry.length - 4} more</span>}
              </p>
            )}
          </div>
          <button
            onClick={() => load(true)}
            disabled={busy}
            data-testid="smart-suggestions-refresh"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white/80 hover:text-white border border-white/20 hover:border-white/50 rounded-full px-4 py-2 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} className={busy ? "animate-spin" : ""}/> {busy ? "Thinking…" : "Refresh"}
          </button>
        </div>

        {busy && !data && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="smart-suggestions-loading">
            {[1,2,3,4].map(i => (
              <div key={i} className="aspect-[4/3] bg-white/5 rounded-xl animate-pulse"/>
            ))}
          </div>
        )}

        {data?.suggestions?.length === 0 && (
          <p className="text-white/70 text-sm" data-testid="smart-suggestions-empty">
            Make your first drop purchase to unlock chef-curated, AI-matched recipes.
          </p>
        )}

        {data?.suggestions?.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="smart-suggestions-grid">
            {data.suggestions.map(s => (
              <Link
                key={s.id}
                to={`/recipes/${s.slug}`}
                className="group block bg-white/5 hover:bg-white/10 backdrop-blur rounded-xl overflow-hidden border border-white/10 transition-all"
                data-testid={`smart-suggestion-${s.slug}`}
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  {s.hero_image && (
                    <img src={s.hero_image} alt={s.title} loading="lazy"
                         className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                  )}
                  <div className="absolute top-2 left-2 bg-[var(--ff-citrus)] text-[var(--ff-text-primary)] text-[10px] font-bold px-2 py-1 rounded-full">
                    {s.match_score}% match
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-display text-base leading-tight">{s.title}</h3>
                  <p className="text-xs text-white/65 mt-1.5 line-clamp-2 italic">"{s.match_reason}"</p>
                  <div className="mt-2 flex items-center gap-3 text-[11px] text-white/60">
                    <span className="inline-flex items-center gap-1"><Clock size={10}/> {s.total_minutes}m</span>
                    <span className="uppercase tracking-wider">{s.difficulty}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function RecipeCard({ r, onFavChange }) {
  return (
    <Link to={`/recipes/${r.slug}`} className="card-flat overflow-hidden group block" data-testid={`recipe-card-${r.slug}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-[var(--ff-muted)]">
        {r.hero_image && <img src={r.hero_image} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>}
        <div className="absolute top-3 right-3">
          <FavBtn slug={r.slug} isFav={r.is_favorite} onChange={onFavChange && (v => onFavChange(r.slug, v))}/>
        </div>
        {r.featured && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[var(--ff-primary)] text-white px-2 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full">
            <Sparkles size={10}/> Featured
          </div>
        )}
      </div>
      <div className="p-5">
        <p className="overline">{r.cuisine || "Curated"}</p>
        <h3 className="font-display text-xl mt-1 group-hover:text-[var(--ff-primary)] transition-colors">{r.title}</h3>
        <p className="text-sm text-[var(--ff-text-secondary)] mt-2 line-clamp-2">{r.summary}</p>
        <div className="mt-4 flex items-center gap-4 text-xs text-[var(--ff-text-muted)]">
          <span className="inline-flex items-center gap-1"><Clock size={12}/> {r.total_minutes} min</span>
          <span className="inline-flex items-center gap-1"><Users size={12}/> {r.servings}</span>
          <span className="uppercase tracking-wider">{r.difficulty}</span>
        </div>
      </div>
    </Link>
  );
}

export function Recipes() {
  const [items, setItems] = useState(null);
  const [collections, setCollections] = useState([]);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (tag) params.set("tag", tag);
    api.get(`/recipes?${params.toString()}`).then(r => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [tag]); // eslint-disable-line
  useEffect(() => {
    api.get("/recipes/collections").then(r => setCollections(r.data)).catch(() => {});
  }, []);

  const onFavChange = (slug, isFav) => setItems(items.map(r => r.slug === slug ? { ...r, is_favorite: isFav } : r));
  const allTags = Array.from(new Set((items || []).flatMap(r => r.tags || []))).sort();

  return (
    <div data-testid="recipes-page">
      <Seo title="Recipes" description="Member-only recipes paired with every drop. Chef-curated, farm-fresh." path="/recipes"/>

      <section className="bg-[var(--ff-bg-alt)]">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-10">
          <p className="overline">Member kitchen</p>
          <h1 className="font-display text-5xl sm:text-6xl mt-3 leading-tight max-w-2xl">
            What to cook<br/>with your drop.
          </h1>
          <p className="mt-4 text-[var(--ff-text-secondary)] max-w-xl leading-relaxed">
            Every drop ships with a chef-tested recipe library. Save your favorites, follow along on video, and discover new ways to use the season's haul.
          </p>
        </div>
      </section>

      <SmartSuggestions/>

      {/* Featured collection */}
      {collections.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-12" data-testid="recipes-collections">
          {collections.map(c => (
            <div key={c.slug} className="mb-12">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <p className="overline">Collection</p>
                  <h2 className="font-display text-3xl mt-1">{c.title}</h2>
                  {c.subtitle && <p className="text-[var(--ff-text-secondary)] mt-2 text-sm">{c.subtitle}</p>}
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {(c.recipes || []).map(r => <RecipeCard key={r.slug} r={r}/>)}
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <h2 className="font-display text-3xl">All recipes</h2>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-muted)]"/>
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                onKeyDown={e => e.key === "Enter" && load()}
                placeholder="Search recipes…"
                className="input-base pl-9 w-full"
                data-testid="recipes-search"
              />
            </div>
            <button onClick={load} className="btn-secondary" data-testid="recipes-search-btn">Search</button>
          </div>
        </div>

        {allTags.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2" data-testid="recipes-tags">
            <button onClick={() => setTag("")} className={`px-3 py-1 text-xs rounded-full ${!tag ? "bg-[var(--ff-primary)] text-white" : "bg-[var(--ff-muted)] hover:bg-[var(--ff-border)]"}`}>All</button>
            {allTags.map(t => (
              <button key={t} onClick={() => setTag(t)} className={`px-3 py-1 text-xs rounded-full capitalize ${tag === t ? "bg-[var(--ff-primary)] text-white" : "bg-[var(--ff-muted)] hover:bg-[var(--ff-border)]"}`} data-testid={`recipes-tag-${t}`}>{t}</button>
            ))}
          </div>
        )}

        {items === null && <p className="text-[var(--ff-text-muted)]">Loading recipes…</p>}
        {items?.length === 0 && <p className="text-[var(--ff-text-muted)]" data-testid="recipes-empty">No recipes match — try another search.</p>}
        {items?.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="recipes-grid">
            {items.map(r => <RecipeCard key={r.slug} r={r} onFavChange={onFavChange}/>)}
          </div>
        )}
      </section>
    </div>
  );
}

export function RecipeDetail() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/recipes/${slug}`).then(res => setR(res.data)).catch(e => setErr(e.response?.data?.detail || "Not found"));
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="recipe-not-found"><h1 className="font-display text-4xl">{err}</h1><Link to="/recipes" className="btn-secondary mt-6 inline-flex">Back to recipes</Link></div>;
  if (!r) return <div className="p-12 text-center text-[var(--ff-text-muted)]" data-testid="recipe-loading">Loading…</div>;

  return (
    <div data-testid={`recipe-detail-${r.slug}`}>
      <Seo title={r.title} description={r.summary} image={r.hero_image} path={`/recipes/${r.slug}`}/>

      <section className="relative min-h-[55vh] flex items-end overflow-hidden">
        {r.hero_image && <img src={r.hero_image} alt={r.title} fetchPriority="high" className="absolute inset-0 w-full h-full object-cover"/>}
        <div className="absolute inset-0 hero-overlay"/>
        <div className="relative max-w-5xl mx-auto px-6 pb-14 text-white w-full">
          {r.chef_name && (
            <div className="inline-flex items-center gap-2 text-sm bg-white/15 backdrop-blur rounded-full px-3 py-1 mb-4">
              <ChefHat size={14}/> {r.chef_name}
            </div>
          )}
          <h1 className="font-display text-5xl sm:text-7xl max-w-3xl leading-[1.02]">{r.title}</h1>
          <p className="mt-4 max-w-2xl text-lg text-white/85 leading-relaxed">{r.summary}</p>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-white/85">
            <span className="inline-flex items-center gap-1.5"><Clock size={15}/> {r.total_minutes} min total · {r.prep_minutes} prep + {r.cook_minutes} cook</span>
            <span className="inline-flex items-center gap-1.5"><Users size={15}/> {r.servings} servings</span>
            <span className="uppercase tracking-wider text-xs bg-white/20 px-2 py-0.5 rounded-full">{r.difficulty}</span>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-14 grid md:grid-cols-3 gap-10">
        <aside className="card-flat p-6 h-fit" data-testid="recipe-ingredients">
          <div className="flex items-center justify-between">
            <p className="overline">Ingredients</p>
            <FavBtn slug={r.slug} isFav={r.is_favorite} onChange={v => setR({...r, is_favorite: v})} data-testid="recipe-fav-btn"/>
          </div>
          <ul className="mt-4 space-y-2 text-sm">
            {(r.ingredients || []).map((it, i) => (
              <li key={i} className="flex items-start gap-2 text-[var(--ff-text-secondary)]">
                <span className="w-1 h-1 rounded-full bg-[var(--ff-primary)] mt-2"/>
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </aside>
        <div className="md:col-span-2">
          <p className="overline">Method</p>
          <div className="mt-4 prose-tight text-[var(--ff-text-primary)] leading-relaxed whitespace-pre-wrap text-[15px]" data-testid="recipe-method">
            {r.body_markdown}
          </div>
          {r.chef_bio && (
            <div className="mt-10 card-flat p-6" data-testid="recipe-chef-spotlight">
              <p className="overline">Chef spotlight</p>
              <h3 className="font-display text-2xl mt-1">{r.chef_name}</h3>
              <p className="text-sm text-[var(--ff-text-secondary)] mt-2">{r.chef_bio}</p>
            </div>
          )}
        </div>
      </section>

      {(r.linked_drops || []).length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pb-16" data-testid="recipe-linked-drops">
          <p className="overline">Get the ingredients</p>
          <h3 className="font-display text-2xl mt-1">Use these drops to cook this recipe.</h3>
          <div className="mt-5 grid md:grid-cols-3 gap-4">
            {r.linked_drops.map(d => (
              <Link key={d.slug} to={`/drops/${d.slug}`} className="card-flat p-4 flex items-center gap-3 hover:border-[var(--ff-primary)]" data-testid={`recipe-link-drop-${d.slug}`}>
                {d.image_url && <img src={d.image_url} alt={d.name} className="w-16 h-16 rounded-lg object-cover"/>}
                <div className="min-w-0">
                  <p className="font-semibold truncate">{d.name}</p>
                  <p className="text-xs text-[var(--ff-text-muted)] capitalize">{d.status} · ${d.price?.toFixed(0)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ---------- Member-dashboard sub-component for the Recipes tab ----------
export function MyRecipesPanel() {
  const [state, setState] = useState(null);
  useEffect(() => { api.get("/recipes/me").then(r => setState(r.data)).catch(() => setState({favorites:[], recently_viewed:[]})); }, []);
  if (!state) return <p className="text-[var(--ff-text-muted)]">Loading your library…</p>;

  const Section = ({ title, list, emptyHint, testId }) => (
    <div data-testid={testId}>
      <h3 className="font-display text-2xl mt-6">{title}</h3>
      {list.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--ff-text-muted)]">{emptyHint}</p>
      ) : (
        <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map(r => <RecipeCard key={r.slug} r={{...r, is_favorite: state.favorites.some(f => f.slug === r.slug)}}/>)}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="my-recipes-panel">
      <Section title="Your favorites" list={state.favorites}
               emptyHint="Tap the heart on any recipe to save it here."
               testId="my-recipes-favorites"/>
      <Section title="Recently viewed" list={state.recently_viewed}
               emptyHint="Browse the recipe library — anything you open shows up here."
               testId="my-recipes-recent"/>
      <Link to="/recipes" className="btn-primary inline-flex items-center gap-2 mt-8" data-testid="my-recipes-explore">Explore all recipes</Link>
    </div>
  );
}
